import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
  buttonFunctions,
} from '../../shared/Button';
import Table from '../../shared/table/Table';
import { selectorTypes } from '../../shared/table/TableRow';
import SvgXIcon from '../../svg/XIcon';
import {
  searchText,
  submitText,
  cancelText,
  unitNameText,
  nameText,
  selectedUnitsText,
} from '../../shared/SharedInternationalizations';
import InlineHelpText from '../../shared/InlineHelpText';
import HelpIcon from '../../shared/HelpIcon';

/**
 * Form for bulk associating of units to a single user.
 * @param {Function} props.onSubmit - Function to be performed when the form is submitted
 * @param {array} props.unitArray - Array of all of the units
 * @param {string} props.user - The id for the user that the units will be associated with
 * @param {object} props.userObj - Object of user information
 * @param {array} props.units - Any units that have been preselected elsewhere and passed to prepopulate the selected units table
 *
 */
export function UnconnectedAssociateUnitsToUserForm(props) {
  const { onSubmit, unitsObj, user, units, userObj, previous } = props;

  const unitArray = Object.values(unitsObj);
  const [unitsFilter, setUnitsFilter] = useState('');
  const [activeUnitFilter, setActiveUnitFilter] = useState('');

  const [showSelectedUnitsHelpText, setShowSelectedUnitsHelpText] = useState(
    false
  );
  const selectedUnitsHelpTextRef = useRef();
  function toggleSelectedUnitsHelpText(value) {
    setShowSelectedUnitsHelpText(value);
  }
  const selectedUnitsHelpText = (
    <FormattedMessage
      id="associateUnitsToUserForm.selectedUnitsHelpText"
      defaultMessage="The units that have been selected. To remove a unit from this list, click the 'x' icon. To add a unit, click in the check box."
      description="Help text."
    />
  );

  const [showActiveUnitHelpText, setShowActiveUnitHelpText] = useState(false);
  const activeUnitHelpTextRef = useRef();
  function toggleActiveUnitHelpText(value) {
    setShowActiveUnitHelpText(value);
  }
  const activeUnitHelpText = (
    <FormattedMessage
      id="associateUnitsToUserForm.activeUnitHelpText"
      defaultMessage="This is the user's active unit."
      description="Help text."
    />
  );

  /**
   * Function to set the search value for active filtering for users
   * @param {object} event - The event from typing in the value to the search bar
   * @fires setUsersFilter - Updates local state with the value typed into the search bar
   */
  const unitsSearchFilter = (event) => {
    const value = event.target.value;
    setUnitsFilter(value);
  };

  const searchFilter = (event) => {
    const value = event.target.value;
    setActiveUnitFilter(value);
  };

  /**
   * Function to generate the units table
   * @returns {object} - The table of units
   */
  const unitsTable = (values) => {
    // map over the units and put into the format that is required for the table component
    const initiallyFilteredUnits = unitArray.filter((item) => {
      if (values.unitsId.includes(item.id)) {
        return false;
      } else if (!item.is_active) {
        return false;
      } else {
        return true;
      }
    });

    const unitsArray = initiallyFilteredUnits.map((unit) => {
      let checked = null;
      return {
        selector: {
          checked: checked,
          sortBy: unit.id,
          display: unit.id,
        },
        name: {
          sortBy: unit.name.toUpperCase(),
          display: unit.name,
        },
      };
    });

    let searchedUnits = [];
    // Take the array and filter based on the text entered in the search bar.
    if (unitsFilter !== '') {
      searchedUnits = unitsArray.filter((item) => {
        // Make the device name uppercase for case insensitivity
        const units = item.name.display.toUpperCase();
        // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
        return units.includes(unitsFilter.toUpperCase());
      });
    } else {
      searchedUnits = unitsArray;
    }

    const tableHeadings = [
      {
        isSortable: false,
        display: '',
      },
      {
        isSortable: true,
        sortBy: 'name',
        display: unitNameText,
      },
    ];

    // generates the actual table
    const table = (
      <Table
        hasTableHead={true}
        hasSelector={true}
        tableHeadings={tableHeadings}
        selectorName="unitsId"
        selectorParamValue="id"
        sortParam="name"
        tableData={searchedUnits}
        selectorType={selectorTypes.checkbox}
        tableStyles="table__form-selector-table"
        // selected={parent_unit}
        data-test="associate-user-units-units-table"
      />
    );

    return table;
  };

  /**
   * Function to generate the parent unit table
   * @returns {object} - The table of parent units
   */
  const selectedUnitsTable = (values) => {
    let table;
    let unitsId = values.unitsId;
    let unitRows = [];
    if (unitsId.length > 0) {
      if (unitsId) {
        const filteredUnits = unitsId.map((id) => {
          let thisId = id;
          if (typeof id === 'string') {
            thisId = parseInt(unitsId);
          }
          const userId = unitsObj[thisId];
          return userId;
        });

        filteredUnits.map((unit) => {// eslint-disable-line
          if (unit) {
            const removeButton = (
              <label>
                <Field
                  className="visually-hidden"
                  name="unitsId"
                  component="input"
                  type="checkbox"
                  value={unit.id}
                />
                <SvgXIcon />
              </label>
            );
            unitRows.push({
              button: {
                sortBy: '',
                display: removeButton,
              },
              name: {
                sortBy: unit.name.toUpperCase(),
                display: unit.name,
              },
            });
            return null;
          }
        });
      }

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },
        {
          isSortable: true,
          sortBy: 'name',
          display: unitNameText,
        },
      ];

      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="name"
          tableData={unitRows}
          tableStyles="table__form-selector-table"
          data-test="associate-user-units-selected-units-table"
        />
      );

      return table;
    } else {
      return '';
    }
  };

  const activeUnitTable = (values) => {
    let active_unit = values.active_unit;
    if (typeof active_unit === 'string') {
      active_unit = parseInt(active_unit);
    }

    const initiallyFilteredUnits = values.unitsId.filter((item) => {
      if (item === active_unit) {
        return false;
      } else {
        return true;
      }
    });
    // map over the units and put into the format that is required for the table component
    const activeUnitArray = initiallyFilteredUnits.map((unit) => {
      const assignedUnit = unitsObj[unit];
      return {
        selector: {
          sortBy: assignedUnit.id,
          display: assignedUnit.id,
        },
        name: {
          sortBy: assignedUnit.name.toUpperCase(),
          display: assignedUnit.name,
        },
      };
    });

    let searchedUnits = [];
    // Take the array and filter based on the text entered in the search bar.
    if (activeUnitFilter !== '') {
      searchedUnits = activeUnitArray.filter((item) => {
        // Make the device name uppercase for case insensitivity
        const parent_unit = item.name.display.toUpperCase();
        // filter based off the includes() value with the entered text (also switched to uppercase) as the filter parameter
        return parent_unit.includes(activeUnitFilter.toUpperCase());
      });
    } else {
      searchedUnits = activeUnitArray;
    }

    const tableHeadings = [
      {
        isSortable: false,
        display: '',
      },
      {
        isSortable: true,
        sortBy: 'name',
        display: nameText,
      },
    ];
    // generates the actual table
    const table = (
      <Table
        hasTableHead={true}
        hasSelector={true}
        tableHeadings={tableHeadings}
        selectorName="active_unit"
        selectorParamValue="id"
        sortParam="name"
        tableData={searchedUnits}
        selectorType={selectorTypes.radio}
        tableStyles="table__constrained table-layout__1-column--selector"
        data-test="add-user-active-unit-table"
      />
    );

    return table;
  };

  /**
   * Function to generate the parent unit table
   * @param {object} values - The current values entered in the form
   * @returns {object} - The table of parent units
   */
  const selectedUnitTable = (values) => {
    let table;
    let active_unit = values.active_unit;

    if (active_unit !== '') {
      if (typeof active_unit === 'string') {
        active_unit = parseInt(active_unit);
      }
      const filteredUnit = unitArray.filter((unit) => {
        if (!values.unitsId.includes(unit.id)) {
          return false;
        } else if (unit.id === active_unit) {
          return true;
        }
        return false;
      });

      const unitRow = filteredUnit.map((unit) => {
        const removeButton = (
          <label>
            <Field
              className="visually-hidden"
              name="active_unit"
              component="input"
              type="radio"
              value=""
            />
            <SvgXIcon />
          </label>
        );
        return {
          button: {
            sortBy: '',
            display: removeButton,
          },
          name: {
            sortBy: unit.name.toUpperCase(),
            display: unit.name,
          },
        };
      });

      const tableHeadings = [
        {
          isSortable: false,
          display: '',
        },
        {
          isSortable: false,

          display: (
            <FormattedMessage
              id="associateUnitsToUserForm.parentUnitSelectedListHeading"
              defaultMessage="Selected Unit"
              description="Header to sort the unit name by"
            />
          ),
        },
      ];
      // generates the actual table
      table = (
        <Table
          hasTableHead={true}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="name"
          tableData={unitRow}
          tableStyles="table__constrained table-layout__1-column--selector"
          data-test="add-user-selected-active-unit-table"
        />
      );

      return table;
    } else {
      return '';
    }
  };

  function displayActiveUnit(values) {
    if (values.unitsId.length > 0) {
      return (
        <div className="form__section-container form__section-container--grid margin-bottom-25">
          <div className="form__item-container">
            <div className="align-center">
              <label className="text-medium text-bold" htmlFor="active_unit">
                <FormattedMessage
                  id="associateUnitsToUserForm.activeUnitLabel"
                  defaultMessage="Active Unit:"
                  description="The active unit of the user"
                />
              </label>
              <HelpIcon
                onClick={() => activeUnitHelpTextRef.current.toggleHelpText()}
                toggled={showActiveUnitHelpText}
                styles="margin-left-5"
              />
            </div>
          </div>
          <div className="width-100 margin-bottom-10">
            <InlineHelpText
              ref={activeUnitHelpTextRef}
              toggleFunction={toggleActiveUnitHelpText}
              message={activeUnitHelpText}
            />
          </div>

          {selectedUnitTable(values)}

          <div className="table__header padding-10">
            <label className="text-medium text-bold" htmlFor="activeUnitFilter">
              {searchText}
            </label>

            <input
              type="text"
              id="activeUnitFilter"
              name="activeUnitFilter"
              onChange={searchFilter}
              className="message-table__filter-search-box margin-left-10"
              value={activeUnitFilter}
              data-test="active-unit-filter"
            />
          </div>

          {activeUnitTable(values)}
        </div>
      );
    } else {
      return '';
    }
  }

  return (
    <React.Fragment>
      <Form
        data-test="associate-units-to-user-form"
        onSubmit={onSubmit}
        initialValues={{
          user: user || [],
          unitsId: units || [],
          active_unit: userObj.active_unit || '',
        }}
        render={({
          handleSubmit,
          pristine,
          invalid,
          reset,
          submitting,
          values,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="associate-users-units__form-container">
            <Field
              className="visually-hidden"
              name="user"
              component="input"
              type="text"
            />
            {displayActiveUnit(values)}
            <div className="form__section-container form__section-container--grid">
              <div className="form__item-container">
                <div className="align-center">
                  <label
                    className="text-medium text-bold form-item__field-message--required"
                    htmlFor="usersId">
                    {selectedUnitsText}
                  </label>
                  <HelpIcon
                    onClick={() =>
                      selectedUnitsHelpTextRef.current.toggleHelpText()
                    }
                    toggled={showSelectedUnitsHelpText}
                    styles="margin-left-5"
                  />
                </div>
              </div>
            </div>
            <div className="width-100 margin-bottom-10">
              <InlineHelpText
                ref={selectedUnitsHelpTextRef}
                toggleFunction={toggleSelectedUnitsHelpText}
                message={selectedUnitsHelpText}
              />
            </div>
            <div className="form__section-container form__section-container--grid margin-bottom-10">
              {selectedUnitsTable(values)}

              <div className="table__header padding-10">
                <label className="text-medium text-bold" htmlFor="usersFilter">
                  {searchText}:
                </label>

                <input
                  type="text"
                  id="unitsFilter"
                  name="unitsFilter"
                  onChange={unitsSearchFilter}
                  className="message-table__filter-search-box margin-left-10"
                  value={unitsFilter}
                  data-test="units-search-filter"
                />
              </div>

              {unitsTable(values)}
            </div>
            <div className="form__section-container form__section-container--spread associate-users-units__form-buttons-container">
              <div className="form__items-group-container" />
              <div className="form__items-group-container">
                <Link
                  className="link link-text"
                  to={previous}
                  data-test="associate-users-to-unit-form-cancel-link">
                  {cancelText}
                </Link>
                <Button
                  buttonFunction={buttonFunctions.submit}
                  text={submitText}
                  buttonType={buttonTypes.success}
                  buttonSize={buttonSizes.small}
                  buttonTextSize={buttonTextSizes.mediumSmall}
                  additionalStyles="no-margin margin-left-15"
                  disabled={pristine || invalid}
                />
              </div>
            </div>
          </form>
        )}
      />
    </React.Fragment>
  );
}

const mapStateToProps = (state, ownProps) => {
  const userObj = state.users.users[ownProps.user];

  return {
    unitsObj: state.units.units,
    userObj,
  };
};
export default connect(mapStateToProps)(UnconnectedAssociateUnitsToUserForm);
